import { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Container, Form, Modal, Nav, Row } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import api, { mediaUrl } from "../../api/axios";
import { ProfileSkeleton } from "../Common/Loading/PageSkeletons";

function CommunityHome() {
  const { slug } = useParams();
  const [business, setBusiness] = useState(null);
  const [posts, setPosts] = useState([]);
  const [products, setProducts] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [tradeDays, setTradeDays] = useState([]);
  const [tradeDayMatches, setTradeDayMatches] = useState({});
  const [tradeDayMatchingConsent, setTradeDayMatchingConsent] = useState({});
  const [communityMatches, setCommunityMatches] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [members, setMembers] = useState([]);
  const [modules, setModules] = useState({});
  const [activeTab, setActiveTab] = useState("feed");
  const [loading, setLoading] = useState(true);
  const [actionStatus, setActionStatus] = useState("");
  const [showMapModal, setShowMapModal] = useState(false);
  const [showHoursModal, setShowHoursModal] = useState(false);
  const [productInterest, setProductInterest] = useState(null);
  const [productInquiry, setProductInquiry] = useState({ quantity: 1, message: "" });
  const storedUser = localStorage.getItem("user");
  const currentUser = storedUser ? JSON.parse(storedUser) : null;

  useEffect(() => {
    setLoading(true);
    api
      .get(`/businesses/${slug}/hub`)
      .then(({ data }) => {
        setBusiness(data.business);
        setPosts(data.posts || []);
        setProducts(data.products || []);
        setTournaments(data.tournaments || []);
        setTradeDays(data.tradeDays || []);
        setRanking(data.ranking || []);
        setMembers(data.members || []);
        setModules(data.modules || {});
      })
      .catch(() => {
        setBusiness(null);
        setPosts([]);
        setProducts([]);
        setTournaments([]);
        setTradeDays([]);
        setTradeDayMatches({});
        setCommunityMatches([]);
        setRanking([]);
        setMembers([]);
        setModules({});
      })
      .finally(() => {
        setLoading(false);
      });
  }, [slug]);

  const isModuleEnabled = (moduleKey, fallback = true) => {
    const module = modules?.[moduleKey];
    if (!module) return fallback;
    return Boolean(module.enabled);
  };
  const feedEnabled = isModuleEnabled("community_feed");
  const productsEnabled = isModuleEnabled("store_products");
  const tournamentsEnabled = isModuleEnabled("store_tournaments");
  const tradeDaysEnabled = isModuleEnabled("trade_days", false);
  const matchingEnabled = isModuleEnabled("matching_engine", false);
  const bindersEnabled = isModuleEnabled("binders");
  const tradesEnabled = isModuleEnabled("trades_cards");
  const visibleTabs = [
    { key: "home", label: "Inicio" },
    feedEnabled && { key: "feed", label: "Feed" },
    productsEnabled && { key: "products", label: "Productos" },
    (bindersEnabled || tradesEnabled || matchingEnabled) && { key: "market", label: "Intereses" },
    tradeDaysEnabled && { key: "tradeDays", label: "Eventos" },
    tournamentsEnabled && { key: "tournaments", label: "Eventos" },
    tournamentsEnabled && { key: "ranking", label: "Ranking" }
  ].filter(Boolean);

  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.some(tab => tab.key === activeTab)) {
      setActiveTab(visibleTabs[0].key);
    }
  }, [activeTab, visibleTabs]);

  const featuredProducts = products.slice(0, 3);
  const recentPosts = posts.slice(0, 3);
  const upcomingEvents = tournaments.slice(0, 2);
  const upcomingTradeDays = tradeDays.slice(0, 2);
  const featuredMembers = members.slice(0, 5);

  const formatPrice = product => {
    if (product.price_cents === null || product.price_cents === undefined) return "Consultar";
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: product.currency || "EUR"
    }).format(product.price_cents / 100);
  };

  const attendTradeDay = (tradeDayId, matchingOptIn) => {
    if (!localStorage.getItem("token")) {
      setActionStatus("Registrate para confirmar asistencia al evento.");
      return;
    }
    setActionStatus("");
    api
      .post(`/businesses/${slug}/trade-days/${tradeDayId}/attend`, {
        bringMode: "trade_cards",
        matchingOptIn: Boolean(matchingOptIn)
      })
      .then(({ data }) => {
        if (!data.matching_opt_in) {
          setTradeDayMatches(current => ({ ...current, [tradeDayId]: [] }));
        }
        setActionStatus(data.status === "waitlisted"
          ? "Estas en lista de espera. El matching se activara cuando se confirme tu plaza."
          : data.matching_opt_in
            ? "Asistencia confirmada y compatibilidad activada."
            : "Asistencia confirmada. Puedes activar la compatibilidad cuando quieras.");
        return api.get(`/businesses/${slug}/hub`);
      })
      .then(({ data }) => setTradeDays(data.tradeDays || []))
      .catch(error => setActionStatus(error.response?.data?.error || "No se pudo confirmar asistencia."));
  };


  const loadCommunityMatches = () => {
    if (!localStorage.getItem("token")) {
      setActionStatus("Registrate para ver compatibilidades de la comunidad.");
      return;
    }
    api
      .get(`/businesses/${slug}/matching`)
      .then(({ data }) => setCommunityMatches(data.recommendations || []))
      .catch(() => setActionStatus("No se pudieron cargar matches de comunidad."));
  };
  const loadTradeDayMatches = tradeDayId => {
    if (!localStorage.getItem("token")) {
      setActionStatus("Registrate para ver compatibilidades de trade.");
      return;
    }
    api
      .get(`/businesses/${slug}/trade-days/${tradeDayId}/recommendations`)
      .then(({ data }) => setTradeDayMatches(current => ({ ...current, [tradeDayId]: data.recommendations || [] })))
      .catch(error => setActionStatus(error.response?.data?.error || "No se pudieron cargar recomendaciones."));
  };
  const registerTournament = tournamentId => {
    setActionStatus("");
    api
      .post(`/businesses/${slug}/tournaments/${tournamentId}/register`)
      .then(({ data }) => {
        setActionStatus(data.status === "waitlisted"
          ? `Estas en lista de espera, posicion ${data.waitlist_position}.`
          : "Asistencia registrada.");
        return api.get(`/businesses/${slug}/hub`);
      })
      .then(({ data }) => {
        setTournaments(data.tournaments || []);
        setTradeDays(data.tradeDays || []);
        setRanking(data.ranking || []);
      })
      .catch(() => {
        setActionStatus("No se pudo registrar la asistencia.");
      });
  };

  const canRegisterTournament = tournament => ["scheduled", "registration_open"].includes(tournament.status);

  const connectStore = () => {
    if (business?.viewer_follows) return;
    setActionStatus("");
    api
      .post(`/social/businesses/${slug}/follow`)
      .then(() => {
        setBusiness(current => ({
          ...current,
          viewer_follows: true,
          followers_count: Number(current?.followers_count || 0) + 1
        }));
        setActionStatus("Ahora sigues esta comunidad.");
      })
      .catch(() => setActionStatus("No se pudo conectar con esta comunidad."));
  };

  const sendProductInquiry = async event => {
    event.preventDefault();
    if (!productInterest) return;
    await api.post(`/businesses/${slug}/products/${productInterest.id}/inquiries`, productInquiry);
    setActionStatus("Consulta enviada a la tienda.");
    setProductInterest(null);
    setProductInquiry({ quantity: 1, message: "" });
  };

  const mapHref = business?.latitude && business?.longitude
    ? `https://www.openstreetmap.org/?mlat=${business.latitude}&mlon=${business.longitude}#map=16/${business.latitude}/${business.longitude}`
    : business?.address_line
      ? `https://www.openstreetmap.org/search?query=${encodeURIComponent([business.address_line, business.city, business.country].filter(Boolean).join(", "))}`
      : "";
  const mapLat = Number(business?.latitude);
  const mapLon = Number(business?.longitude);
  const mapDelta = 0.004;
  const mapEmbedSrc = business?.latitude && business?.longitude
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${mapLon - mapDelta},${mapLat - mapDelta},${mapLon + mapDelta},${mapLat + mapDelta}&layer=mapnik&marker=${mapLat},${mapLon}`
    : "";
  const socialLinks = [
    { label: "Web", url: business?.website_url, icon: "bi-globe2" },
    { label: "Instagram", url: business?.instagram_url, icon: "bi-instagram" },
    { label: "Facebook", url: business?.social_links?.facebook, icon: "bi-facebook" },
    { label: "TikTok", url: business?.social_links?.tiktok, icon: "bi-tiktok" },
    { label: "X", url: business?.social_links?.x, icon: "bi-twitter-x" },
    { label: "Discord", url: business?.social_links?.discord, icon: "bi-discord" },
    { label: "WhatsApp", url: business?.social_links?.whatsapp, icon: "bi-whatsapp" }
  ].filter(({ url }) => Boolean(url));
  const dayLabels = [
    ["monday", "Lunes"],
    ["tuesday", "Martes"],
    ["wednesday", "Miercoles"],
    ["thursday", "Jueves"],
    ["friday", "Viernes"],
    ["saturday", "Sabado"],
    ["sunday", "Domingo"]
  ];
  const rawOpeningHours = business?.opening_hours;
  const structuredHours = rawOpeningHours?.days || (rawOpeningHours && typeof rawOpeningHours === "object" ? rawOpeningHours : {});
  const hoursSummary = dayLabels.map(([key, label]) => ({ key, label, value: structuredHours[key] || {} }));
  const hasStructuredHours = hoursSummary.some(day => day.value.closed || day.value.open || day.value.close);
  const todayKey = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"][new Date().getDay()];
  const todayHours = hoursSummary.find(day => day.key === todayKey);
  const todayHoursLabel = todayHours?.value?.closed
    ? "Hoy cerrado"
    : todayHours?.value?.open || todayHours?.value?.close
      ? `Hoy ${todayHours.value.open || "--"} - ${todayHours.value.close || "--"}`
      : "";
  const isStoreManager = currentUser?.business_id === business?.id
    || currentUser?.businessId === business?.id
    || currentUser?.id === business?.owner_user_id
    || (currentUser?.email && business?.email && currentUser.email.toLowerCase() === business.email.toLowerCase());
  const canAdminStore = currentUser?.role === "super_admin" || (currentUser?.role === "store_admin" && isStoreManager);

  if (loading) {
    return (
      <Container className="page-band store-page"><ProfileSkeleton /></Container>
    );
  }

  return (
    <Container className="page-band store-page">
      <div className="store-hero mb-4">
        <div className="store-cover">
          {business?.cover_url && <img src={mediaUrl(business.cover_url)} alt="" />}
        </div>
        {business?.logo_url && <img className="store-logo" src={mediaUrl(business.logo_url)} alt="" />}
      </div>
      <div className="store-header mb-4">
        <div>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <h1 className="section-title mb-1">{business?.name || slug}</h1>
            {business?.is_verified && <Badge bg="success">Verificada</Badge>}
            {business?.verification_status && !business?.is_verified && (
              <Badge bg="light" text="dark">{business.verification_status}</Badge>
            )}
          </div>
          <p className="muted-copy mb-0">{business?.description || "Comunidad local Cardora"}</p>
          <div className="store-profile-lines mt-2">
            {[business?.address_line, business?.city, business?.country].filter(Boolean).length > 0 && (
              <span>{[business.address_line, business.city, business.country].filter(Boolean).join(", ")}</span>
            )}
            {business?.phone && <span>{business.phone}</span>}
            {business?.email && <span>{business.email}</span>}
            {todayHoursLabel && <span className="store-today-hours">{todayHoursLabel}</span>}
            {hasStructuredHours && (
              <button type="button" className="store-link-button" onClick={() => setShowHoursModal(true)}>Ver horarios</button>
            )}
            {mapHref && (
              <button type="button" className="store-link-button" onClick={() => setShowMapModal(true)}>Como llegar</button>
            )}
          </div>
          {socialLinks.length > 0 && (
            <div className="store-social-links" aria-label="Redes sociales">
              {socialLinks.map(({ label, url, icon }) => (
                <a href={url} target="_blank" rel="noreferrer" key={label} title={label}>
                  <i className={`bi ${icon}`} />
                  <span>{label}</span>
                </a>
              ))}
            </div>
          )}
        </div>
        <div className="d-flex gap-2 justify-content-end">
          {localStorage.getItem("token") ? (
            business?.viewer_follows ? (
              <span className="store-following-pill">Siguiendo</span>
            ) : (
              <Button variant="outline-dark" size="sm" onClick={connectStore}>
                Seguir comunidad
              </Button>
            )
          ) : (
            <Button as={Link} to="/register" variant="outline-dark" size="sm">
              Registrarme para conectar
            </Button>
          )}
          {canAdminStore && (
            <Button as={Link} to={`/admin/${slug}`} variant="outline-secondary" size="sm">
              Admin
            </Button>
          )}
        </div>
      </div>

      <Row className="g-3 mb-4">
        <Col md={4}>
          <div className="store-metric">
            <span>Seguidores</span>
            <strong>{business?.followers_count || 0}</strong>
          </div>
        </Col>
        {productsEnabled && (
          <Col md={4}>
            <div className="store-metric">
              <span>Productos</span>
              <strong>{products.length}</strong>
            </div>
          </Col>
        )}
        {(bindersEnabled || tradesEnabled) && (
          <Col md={4}>
            <div className="store-metric">
              <span>Comunidad trade</span>
              <strong>{tradesEnabled ? "Activa" : "Binder"}</strong>
            </div>
          </Col>
        )}
        {tradeDaysEnabled && (
          <Col md={4}>
            <div className="store-metric">
              <span>Eventos trade</span>
              <strong>{tradeDays.length}</strong>
            </div>
          </Col>
        )}
        {tournamentsEnabled && (
          <Col md={4}>
            <div className="store-metric">
              <span>Eventos</span>
              <strong>{tournaments.length}</strong>
            </div>
          </Col>
        )}
      </Row>

      <Nav variant="tabs" activeKey={activeTab} onSelect={key => setActiveTab(key || visibleTabs[0]?.key || "feed")} className="binder-tabs mb-4">
        {visibleTabs.map(tab => (
          <Nav.Item key={tab.key}>
            <Nav.Link eventKey={tab.key}>{tab.label}</Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      {actionStatus && <div className="inline-page-notice mb-3">{actionStatus}</div>}

      {activeTab === "home" && (
        <div className="store-home-grid">
          <Card className="cardora-card store-home-main">
            <Card.Body>
              <div className="store-home-eyebrow">Comunidad</div>
              <Card.Title>Todo lo que pasa en {business?.name || "esta tienda"}</Card.Title>
              <Card.Text className="muted-copy">
                Sigue la tienda para enterarte de productos, pulls, cartas buscadas, trades y eventos de la comunidad.
              </Card.Text>
              <div className="store-home-actions">
                {localStorage.getItem("token") ? (
                  business?.viewer_follows ? (
                    <span className="store-following-pill">Siguiendo</span>
                  ) : (
                    <Button className="btn-cardora" onClick={connectStore}>Seguir comunidad</Button>
                  )
                ) : (
                  <Button as={Link} to="/register" className="btn-cardora">Registrarme</Button>
                )}
                {tradesEnabled && <Button as={Link} to="/trades" variant="outline-dark">Ver trades</Button>}
                {tradeDaysEnabled && <Button variant="outline-dark" onClick={() => setActiveTab("tradeDays")}>Eventos trade</Button>}
                {bindersEnabled && <Button as={Link} to="/binder" variant="outline-dark">Mi binder</Button>}
              </div>
            </Card.Body>
          </Card>

          <Card className="cardora-card store-home-panel">
            <Card.Body>
              <div className="store-home-panel-head">
                <Card.Title>Actividad reciente</Card.Title>
                {feedEnabled && <Button variant="link" onClick={() => setActiveTab("feed")}>Ver feed</Button>}
              </div>
              {recentPosts.length === 0 && <p className="muted-copy mb-0">Aun no hay publicaciones.</p>}
              {recentPosts.map(post => (
                <div className="store-home-activity" key={post.id}>
                  <Badge bg="light" text="dark">{post.type}</Badge>
                  <Link to={`/u/${post.user_id}`}>{post.display_name}</Link>
                  <span>{post.body || "Publicacion sin texto"}</span>
                </div>
              ))}
            </Card.Body>
          </Card>

          {productsEnabled && (
            <Card className="cardora-card store-home-panel">
              <Card.Body>
                <div className="store-home-panel-head">
                  <Card.Title>Catalogo de la tienda</Card.Title>
                  <Button variant="link" onClick={() => setActiveTab("products")}>Ver catalogo</Button>
                </div>
                <div className="store-home-products">
                  {featuredProducts.length === 0 && <p className="muted-copy mb-0">La tienda aun no publico catalogo.</p>}
                  {featuredProducts.map(product => (
                    <button type="button" className="store-home-product" key={product.id} onClick={() => setProductInterest(product)}>
                      {(product.images?.[0]?.image_url || product.image_url) && <img src={mediaUrl(product.images?.[0]?.image_url || product.image_url)} alt="" />}
                      <span>
                        <strong>{product.name}</strong>
                        <small>{formatPrice(product)}</small>
                      </span>
                    </button>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}

          {(tradesEnabled || bindersEnabled) && (
            <Card className="cardora-card store-home-panel">
              <Card.Body>
                <div className="store-home-panel-head">
                  <Card.Title>Intereses y coleccion</Card.Title>
                  <Button variant="link" onClick={() => setActiveTab("market")}>Abrir</Button>
                </div>
                <div className="store-home-trade-strip">
                  {tradesEnabled && <span>Propuestas carta/carta</span>}
                  {isModuleEnabled("trades_hybrid", false) && <span>Encuentros flexibles</span>}
                  {bindersEnabled && <span>Binders publicos</span>}
                </div>
                <p className="muted-copy mb-0">La idea es que la gente encuentre cartas, proponga trades y coordine en la tienda.</p>
              </Card.Body>
            </Card>
          )}

          {tradeDaysEnabled && (
            <Card className="cardora-card store-home-panel">
              <Card.Body>
                <div className="store-home-panel-head">
                  <Card.Title>Eventos trade</Card.Title>
                  <Button variant="link" onClick={() => setActiveTab("tradeDays")}>Ver eventos</Button>
                </div>
                {upcomingTradeDays.length === 0 && <p className="muted-copy mb-0">Aun no hay eventos trade publicados.</p>}
                {upcomingTradeDays.map(event => (
                  <div className="store-home-event" key={event.id}>
                    <strong>{event.name}</strong>
                    <span>{event.starts_at ? new Date(event.starts_at).toLocaleString("es-ES") : "Fecha por confirmar"}</span>
                    <small>{event.attendees_count || 0} asistentes</small>
                  </div>
                ))}
              </Card.Body>
            </Card>
          )}

          {tournamentsEnabled && (
            <Card className="cardora-card store-home-panel">
              <Card.Body>
                <div className="store-home-panel-head">
                  <Card.Title>Eventos</Card.Title>
                  <Button variant="link" onClick={() => setActiveTab("tournaments")}>Ver eventos</Button>
                </div>
                {upcomingEvents.length === 0 && <p className="muted-copy mb-0">No hay eventos publicados.</p>}
                {upcomingEvents.map(event => (
                  <div className="store-home-event" key={event.id}>
                    <strong>{event.name}</strong>
                    <span>{event.starts_at ? new Date(event.starts_at).toLocaleString("es-ES") : "Fecha por confirmar"}</span>
                  </div>
                ))}
              </Card.Body>
            </Card>
          )}

          <Card className="cardora-card store-home-panel">
            <Card.Body>
              <div className="store-home-panel-head">
                <Card.Title>Miembros</Card.Title>
                <span className="muted-copy">{members.length} recientes</span>
              </div>
              <div className="store-member-strip">
                {featuredMembers.length === 0 && <p className="muted-copy mb-0">Aun no hay miembros conectados.</p>}
                {featuredMembers.map(member => (
                  <Link to={`/u/${member.id}`} className="store-member-pill" key={member.id}>
                    {member.avatar_url ? <img src={mediaUrl(member.avatar_url)} alt="" /> : <span>{(member.display_name || "?").slice(0, 1)}</span>}
                    <strong>{member.display_name}</strong>
                    {member.reviews_count > 0 && <small>{member.average_rating} / 5</small>}
                  </Link>
                ))}
              </div>
            </Card.Body>
          </Card>
        </div>
      )}

      {feedEnabled && activeTab === "feed" && (
        <div className="d-grid gap-3">
          {posts.length === 0 && (
            <Card className="cardora-card">
              <Card.Body>Aun no hay comunicados en esta tienda.</Card.Body>
            </Card>
          )}
          {posts.map(post => (
            <Card className="cardora-card feed-card" key={post.id}>
              <Card.Body>
                <div className="d-flex justify-content-between gap-3 mb-2">
                  <Link className="fw-semibold" to={`/u/${post.user_id}`}>
                    {post.display_name}
                  </Link>
                  <Badge bg="light" text="dark">{post.type}</Badge>
                </div>
                <p>{post.body}</p>
                {(post.metadata?.tournamentId || post.metadata?.tradeId || post.metadata?.collectionId) && (
                  <div className="feed-link-row">
                    {post.metadata?.tournamentId && (
                      <Button as={Link} to={`/tournaments/${post.metadata.tournamentId}`} size="sm" variant="outline-dark">
                        Ver evento
                      </Button>
                    )}
                    {post.metadata?.tradeId && (
                      <Button as={Link} to="/trades" size="sm" variant="outline-dark">
                        Ver trade
                      </Button>
                    )}
                    {post.metadata?.collectionId && (
                      <Button as={Link} to={`/u/${post.user_id}?tab=binder&collection=${post.metadata.collectionId}`} size="sm" variant="outline-dark">
                        Ver binder
                      </Button>
                    )}
                  </div>
                )}
                {post.images?.length > 0 && (
                  <div className={`feed-image-grid ${post.images.length === 1 ? "single" : ""}`}>
                    {post.images.map(image => (
                      <img src={mediaUrl(image.image_url)} alt="" key={image.id} />
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      {productsEnabled && activeTab === "products" && (
        <Row className="g-3">
          {products.length === 0 && (
            <Col>
              <Card className="cardora-card">
                <Card.Body>No hay productos publicados en el catalogo.</Card.Body>
              </Card>
            </Col>
          )}
          {products.map(product => (
            <Col md={6} lg={4} key={product.id}>
              <Card className="cardora-card h-100 store-product">
                {(product.images?.[0]?.image_url || product.image_url) && (
                  <img src={mediaUrl(product.images?.[0]?.image_url || product.image_url)} alt={product.name} />
                )}
                <Card.Body>
                  <div className="d-flex justify-content-between gap-2 mb-2">
                    <Card.Title>{product.name}</Card.Title>
                    <Badge bg="light" text="dark">
                      {product.status}
                    </Badge>
                  </div>
                  <Card.Text className="muted-copy">{product.description || product.product_type}</Card.Text>
                  <div className="store-product-footer">
                    <strong>{formatPrice(product)}</strong>
                    <span>{product.quantity > 0 ? `${product.quantity} disp.` : "Consultar stock"}</span>
                  </div>
                  {localStorage.getItem("token") ? (
                    <Button
                      className="btn-cardora w-100 mt-3"
                      disabled={product.status !== "available" || product.quantity <= 0}
                      onClick={() => {
                        setProductInterest(product);
                        setProductInquiry({ quantity: 1, message: "" });
                      }}
                    >
                      Consultar / me interesa
                    </Button>
                  ) : (
                    <Button as={Link} to="/register" variant="outline-dark" className="w-100 mt-3">
                      Registrarme para consultar
                    </Button>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}


      {activeTab === "market" && (
        <Row className="g-3">
          {tradesEnabled && (
            <Col md={6}>
              <Card className="cardora-card h-100">
                <Card.Body>
                  <Badge bg="light" text="dark" className="mb-2">Intereses</Badge>
                  <Card.Title>Oportunidades entre coleccionistas</Card.Title>
                  <Card.Text className="muted-copy">
                    Descubre personas compatibles por cartas buscadas, binders y eventos de esta comunidad.
                  </Card.Text>
                  <div className="trade-action-row">
                    <Button as={Link} to="/trades" className="btn-cardora">Ver mis intereses</Button>
                    {matchingEnabled && <Button variant="outline-dark" onClick={loadCommunityMatches}>Buscar oportunidades</Button>}
                  </div>
                  {communityMatches.length > 0 && (
                    <div className="trade-day-matches mt-3">
                      <h3>Oportunidades de comunidad</h3>
                      {communityMatches.map(match => (
                        <Link to={`/u/${match.user_id}`} className="trade-day-match" key={match.user_id}>
                          <span>
                            <strong>{match.display_name}</strong>
                            <small>{match.real_photo_matches > 0 ? `${match.real_photo_matches} con foto real · ` : ""}Tiene {match.has_cards_i_want} que buscas · podria interesarle {match.wants_cards_i_have} de ti</small>
                          </span>
                          <b>{match.compatibility_score}%</b>
                        </Link>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          )}
          {bindersEnabled && (
            <Col md={6}>
              <Card className="cardora-card h-100">
                <Card.Body>
                  <Badge bg="light" text="dark" className="mb-2">Binders</Badge>
                  <Card.Title>Binders de la comunidad</Card.Title>
                  <Card.Text className="muted-copy">
                    Usa la busqueda para encontrar cartas que otros tienen, buscan o muestran en sus binders.
                  </Card.Text>
                  <Button as={Link} to="/binder" variant="outline-dark">
                    Gestionar mi binder
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>
      )}


      {tradeDaysEnabled && activeTab === "tradeDays" && (
        <div className="d-grid gap-3">
          {tradeDays.length === 0 && (
            <Card className="cardora-card">
              <Card.Body>No hay eventos trade publicados todavia.</Card.Body>
            </Card>
          )}
          {tradeDays.map(event => {
            const matches = tradeDayMatches[event.id] || [];
            return (
              <Card className="cardora-card trade-day-card" key={event.id}>
                <Card.Body>
                  <div className="trade-card-header">
                    <div>
                      <Badge bg="light" text="dark" className="mb-2">Evento trade</Badge>
                      <Card.Title>{event.name}</Card.Title>
                      <p className="muted-copy mb-0">{event.description || "Confirma asistencia, prepara tu binder y descubre posibles trades antes de ir a la tienda."}</p>
                    </div>
                    <div className="trade-summary-pills">
                      <span className="trade-pill is-status">{event.status}</span>
                      <span className="trade-pill is-cash">{event.attendees_count || 0} asistentes</span>
                    </div>
                  </div>
                  <div className="store-home-event mt-3">
                    <strong>{event.starts_at ? new Date(event.starts_at).toLocaleString("es-ES") : "Fecha por confirmar"}</strong>
                    <span>{event.capacity ? `Cupo ${event.capacity}` : "Sin cupo definido"}</span>
                  </div>
                  <div className="trade-action-row mt-3">
                    {event.viewer_attending ? (
                      <span className="store-following-pill">
                        {event.viewer_attendance_status === "waitlisted" ? "Lista de espera" : "Asistencia confirmada"}
                      </span>
                    ) : (
                      <Button
                        className="btn-cardora"
                        onClick={() => attendTradeDay(event.id, tradeDayMatchingConsent[event.id])}
                      >
                        Confirmar asistencia
                      </Button>
                    )}
                    {matchingEnabled && event.viewer_attending && !event.viewer_matching_opt_in && event.viewer_attendance_status !== "waitlisted" && (
                      <Button variant="outline-dark" onClick={() => attendTradeDay(event.id, true)}>Activar compatibilidad</Button>
                    )}
                    {matchingEnabled && event.viewer_matching_opt_in && event.viewer_attendance_status !== "waitlisted" && (
                      <Button variant="outline-dark" onClick={() => loadTradeDayMatches(event.id)}>Ver compatibilidad</Button>
                    )}
                    {matchingEnabled && event.viewer_matching_opt_in && (
                      <Button variant="link" className="text-muted" onClick={() => attendTradeDay(event.id, false)}>
                        Desactivar compatibilidad
                      </Button>
                    )}
                    <Button as={Link} to="/binder" variant="outline-dark">Preparar binder</Button>
                  </div>
                  {matchingEnabled && !event.viewer_attending && (
                    <Form.Check
                      className="mt-3"
                      type="switch"
                      id={`trade-day-matching-${event.id}`}
                      checked={Boolean(tradeDayMatchingConsent[event.id])}
                      onChange={change => setTradeDayMatchingConsent(current => ({
                        ...current,
                        [event.id]: change.target.checked
                      }))}
                      label="Quiero descubrir asistentes compatibles y aparecer en sus recomendaciones"
                    />
                  )}
                  {matchingEnabled && event.viewer_matching_opt_in && event.viewer_attendance_status !== "waitlisted" && (
                    <small className="d-block mt-2 text-success">Compatibilidad activa para este evento.</small>
                  )}
                  {matches.length > 0 && (
                    <div className="trade-day-matches mt-3">
                      <h3>Compatibilidades</h3>
                      {matches.map(match => (
                        <Link to={`/u/${match.user_id}`} className="trade-day-match" key={match.user_id}>
                          <span>
                            <strong>{match.display_name}</strong>
                            <small>{match.real_photo_matches > 0 ? `${match.real_photo_matches} con foto real · ` : ""}Tiene {match.has_cards_i_want} que buscas · podria interesarle {match.wants_cards_i_have} de ti</small>
                          </span>
                          <b>{match.compatibility_score}%</b>
                        </Link>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            );
          })}
        </div>
      )}
      {tournamentsEnabled && activeTab === "tournaments" && (
        <div className="d-grid gap-3">
          {tournaments.length === 0 && (
            <Card className="cardora-card">
              <Card.Body>No hay eventos publicados.</Card.Body>
            </Card>
          )}
          {tournaments.map(tournament => (
            <Card className="cardora-card" key={tournament.id}>
              <Card.Body>
                <div className="d-flex flex-wrap justify-content-between gap-3">
                  <div>
                    <Card.Title>{tournament.name}</Card.Title>
                    <Card.Text className="muted-copy mb-2">{tournament.description || "Evento de tienda"}</Card.Text>
                    <div className="store-inline-stats">
                      <span>{tournament.starts_at ? new Date(tournament.starts_at).toLocaleString("es-ES") : "Fecha por confirmar"}</span>
                      <span>{tournament.players_count || 0}/{tournament.capacity || "sin limite"}</span>
                      <span>{tournament.entry_fee_cents ? formatPrice({ price_cents: tournament.entry_fee_cents, currency: "EUR" }) : "Gratis/consultar"}</span>
                    </div>
                  </div>
                  <div className="d-grid gap-2 align-content-start">
                    {tournament.viewer_is_registered || tournament.viewer_is_waitlisted ? (
                      <Button variant={tournament.viewer_is_waitlisted ? "outline-warning" : "outline-success"} disabled>
                        {tournament.viewer_is_waitlisted
                          ? `Lista de espera #${tournament.viewer_waitlist_position || "-"}`
                          : "Asistencia confirmada"}
                      </Button>
                    ) : localStorage.getItem("token") && canRegisterTournament(tournament) ? (
                      <Button className="btn-cardora" onClick={() => registerTournament(tournament.id)}>
                        {tournament.capacity && Number(tournament.players_count || 0) >= Number(tournament.capacity)
                          ? "Unirme a lista de espera"
                          : "Inscribirme"}
                      </Button>
                    ) : !localStorage.getItem("token") ? (
                      <Button as={Link} to="/register" className="btn-cardora">
                        Registrarme para inscribirme
                      </Button>
                    ) : (
                      <Button variant="outline-dark" disabled>
                        {tournament.capacity && Number(tournament.players_count || 0) >= Number(tournament.capacity)
                          ? "Cupo completo"
                          : "Asistencia cerrada"}
                      </Button>
                    )}
                    <Button as={Link} to={`/tournaments/${tournament.id}`} variant="outline-dark">
                      Ver evento
                    </Button>
                    {tournament.external_ranking_url && (
                      <Button as="a" href={tournament.external_ranking_url} target="_blank" rel="noreferrer" variant="outline-secondary">
                        Ranking oficial
                      </Button>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      {tournamentsEnabled && activeTab === "ranking" && (
        <Card className="cardora-card">
          <Card.Body>
            {ranking.length === 0 && <div>Aun no hay ranking interno.</div>}
            {ranking.map((player, index) => (
              <div className="store-ranking-row" key={player.user_id}>
                <span>{index + 1}</span>
                <Link to={`/u/${player.user_id}`}>{player.display_name}</Link>
                <strong>{player.points} pts</strong>
                <small>
                  {player.wins}V {player.losses}D {player.draws}E
                </small>
              </div>
            ))}
          </Card.Body>
        </Card>
      )}

      <Modal show={showHoursModal} onHide={() => setShowHoursModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Horarios</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {typeof business?.opening_hours === "string" && <p className="muted-copy">{business.opening_hours}</p>}
          {business?.opening_hours?.text && <p className="muted-copy">{business.opening_hours.text}</p>}
          <div className="store-hours-grid is-modal">
            {hoursSummary.map(({ key, label, value: day }) => {
              const hoursLabel = day?.closed ? "Cerrado" : day?.open || day?.close ? `${day.open || "--"} - ${day.close || "--"}` : "Sin definir";
              return (
                <div className={`store-hour-row ${key === todayKey ? "is-today" : ""}`} key={key}>
                  <span>{label}</span>
                  <strong>{hoursLabel}</strong>
                </div>
              );
            })}
          </div>
        </Modal.Body>
      </Modal>
      <Modal show={showMapModal} onHide={() => setShowMapModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Como llegar</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mapEmbedSrc ? (
            <div className="store-map-modal">
              <iframe title={`Mapa de ${business?.name || slug}`} src={mapEmbedSrc} loading="lazy" />
            </div>
          ) : (
            <p className="muted-copy mb-0">{[business?.address_line, business?.city, business?.country].filter(Boolean).join(", ")}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          {mapHref && (
            <Button as="a" href={mapHref} target="_blank" rel="noreferrer" className="btn-cardora">
              Abrir mapa
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(productInterest)} onHide={() => setProductInterest(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Consultar en tienda</Modal.Title>
        </Modal.Header>
        <Form onSubmit={sendProductInquiry}>
          <Modal.Body>
            <p className="muted-copy">Envia una consulta por <strong>{productInterest?.name}</strong>. La tienda podra responderte y coordinar por comunidad.</p>
            <Form.Group className="mb-3">
              <Form.Label>Cantidad</Form.Label>
              <Form.Control
                type="number"
                min="1"
                max={productInterest?.quantity || 1}
                value={productInquiry.quantity}
                onChange={event => setProductInquiry({ ...productInquiry, quantity: Number(event.target.value) })}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Mensaje para la tienda</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={productInquiry.message}
                onChange={event => setProductInquiry({ ...productInquiry, message: event.target.value })}
                placeholder="Consulta disponibilidad, retiro o reserva..."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-dark" onClick={() => setProductInterest(null)}>Cancelar</Button>
            <Button type="submit" className="btn-cardora">Enviar interes</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default CommunityHome;


